import React, { useMemo } from "react";
import { EmptyState } from "../EmptyState";
import FileQueueItem from "./FileQueueItem";
import { Separator } from "@exiftrator/shared/components/ui";
import { WORKSPACE_SPACING } from "../Workspace/workspaceSpacing";
import { cn } from "../utils/cn";

function FileQueueList({
  sections,
  selectedIndices,
  primaryIndex,
  onItemClick,
  onItemDragStart,
  onDrop,
  onDragOver,
  onKeyboardSelect,
  scrollContainerRef,
  compact = false,
  emptyTitle = "Drag archives or click Select to begin",
}) {
  const orderedIndices = useMemo(
    () => sections.flatMap((section) => section.rows.map((row) => row.index)),
    [sections],
  );

  const handleKeyDown = (event) => {
    if (orderedIndices.length === 0) return;

    const currentPosition = orderedIndices.indexOf(primaryIndex);

    if (event.key === "ArrowDown") {
      event.preventDefault();
      event.stopPropagation();
      const nextPos =
        currentPosition < 0
          ? 0
          : Math.min(orderedIndices.length - 1, currentPosition + 1);
      onKeyboardSelect(
        orderedIndices[nextPos],
        event.shiftKey && currentPosition >= 0,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      const nextPos =
        currentPosition < 0 ? 0 : Math.max(0, currentPosition - 1);
      onKeyboardSelect(
        orderedIndices[nextPos],
        event.shiftKey && currentPosition >= 0,
      );
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      event.stopPropagation();
      onKeyboardSelect(
        orderedIndices[0],
        event.shiftKey && currentPosition >= 0,
      );
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      event.stopPropagation();
      onKeyboardSelect(
        orderedIndices[orderedIndices.length - 1],
        event.shiftKey && currentPosition >= 0,
      );
      return;
    }

    if (
      event.key === "Enter" &&
      primaryIndex >= 0 &&
      orderedIndices.includes(primaryIndex)
    ) {
      event.preventDefault();
      event.stopPropagation();
      onKeyboardSelect(primaryIndex, false);
    }
  };

  return (
    <div
      ref={scrollContainerRef}
      className={cn(
        "min-h-0 flex-1 overflow-y-auto overflow-x-hidden outline-none focus:outline-none scrollbar-thin scrollbar-thumb-text-tertiary/20",
        WORKSPACE_SPACING.listRailInset,
      )}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onMouseDown={(event) => {
        if (event.currentTarget !== document.activeElement) {
          event.currentTarget.focus();
        }
      }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {orderedIndices.length === 0 ? (
        <EmptyState
          icon="🎞️"
          title={emptyTitle}
          className="h-full animate-pulse"
        />
      ) : (
        <div className="space-y-1">
          {sections.map((section) => (
            <div key={section.id} className="space-y-1">
              {section.label && (
                <div className="py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                      {section.label}
                    </span>
                    <Separator className="flex-1 bg-border-subtle" />
                  </div>
                </div>
              )}

              <div className="space-y-1">
                {section.rows.map((row) => (
                  <FileQueueItem
                    key={row.index}
                    row={row}
                    compact={compact}
                    isSelected={selectedIndices.has(row.index)}
                    onClick={onItemClick}
                    onDragStart={onItemDragStart}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FileQueueList;
